import React from 'react'

import { Box, Checkbox, Tag, Text } from '@factorialco/deprecated-design-system'
import { WorkSpaceSlotFragment } from 'generated/resources/graphql'
import * as momentTimezone from 'moment-timezone'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import type { WorkSpaceSlotNode } from 'modules/space_control/hooks/useWorkSpaceSlots'

interface Props {
  slots: WorkSpaceSlotNode[]
  onChange: (value: number) => void
  selectedValues: number[]
  selectedDate: string
  isHalfDay?: boolean
  timezone?: string | null
}

const BookingSlots = ({
  slots,
  onChange,
  selectedValues,
  selectedDate,
  isHalfDay = false,
  timezone = 'Europe/Madrid',
}: Props) => {
  const getLabel = (index: number, isHalfDay: boolean, slot: WorkSpaceSlotNode) => {
    if (isHalfDay) {
      return index === 0
        ? i18n.t('space_control.overview.booking_sidepanel.time_slots.first_half')
        : i18n.t('space_control.overview.booking_sidepanel.time_slots.second_half')
    } else {
      return `${slot.startsAt} - ${slot.endsAt}`
    }
  }

  const isBooked = (slot: WorkSpaceSlotFragment) => slot.currentCapacity === 0

  const isExpired = (slot: WorkSpaceSlotFragment) => {
    if (!timezone) return false

    const nowInSpaceLocation = momentTimezone.tz(timezone)

    const [hour, minute] = slot.endsAt.split(':').map(Number)
    if (hour === undefined) return false
    const slotEndTime = momentTimezone.tz(selectedDate, timezone).set({ hour, minute })

    return nowInSpaceLocation.isAfter(slotEndTime)
  }

  return (
    <Box gap="s4">
      <Text.Normal size="200" weight="medium" color="grey1000">
        {i18n.t('space_control.overview.booking_sidepanel.time_slots.label')}
      </Text.Normal>
      <Box
        borderRadius={{ all: 'abs008' }}
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        gapDivider
        role="list"
        aria-label="slots"
      >
        {slots
          .filter((slot) => !isExpired(slot))
          .map((slot, i) => (
            <Box
              key={i}
              flexDirection="row"
              gap="s8"
              alignItems="center"
              justifyContent="spaceBetween"
              padding="s16"
              background={isBooked(slot) ? 'grey100' : undefined}
              role="listitem"
              aria-label={getLabel(i, isHalfDay, slot)}
            >
              <Checkbox
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onChange(Number(event.target.value))
                }
                value={slot.slotNumber}
                checked={selectedValues.includes(slot.slotNumber)}
                disabled={isBooked(slot)}
                label={getLabel(i, isHalfDay, slot)}
              />
              {isBooked(slot) && (
                <Tag type="grey">
                  {i18n.t('space_control.overview.booking_sidepanel.time_slots.is_booked')}
                </Tag>
              )}
            </Box>
          ))}
      </Box>
    </Box>
  )
}

export default observer(BookingSlots)
